import { useEffect, useRef } from 'react';
import { MotifToaster } from '@ey-xd/motif-wc-react';
import { useToastStore } from './toast-store';

/**
 * Minimal structural view of the underlying `<motif-toaster>` element's
 * imperative API (`@ey-xd/motif-components` `MotifToaster`). The component
 * exposes NO content slot: toasts are added with `addToast` and removed with
 * `removeToast`, and the element owns rendering, positioning and auto-dismiss.
 */
interface ToasterElement {
  addToast(
    toast: {
      title?: string;
      supportText?: string;
      variant?: 'info' | 'success' | 'warning' | 'error' | 'custom';
      type?: 'text' | 'badge-group';
      onDismissed?: () => void;
    },
    duration?: number,
  ): Promise<number>;
  removeToast(id: number): Promise<void>;
}

/**
 * Renders the toast queue from `toast-store`. Mounted once in `ShellLayout`.
 *
 * `motif-toaster` is an IMPERATIVE component — it has no content slot, only
 * `addToast()` / `removeToast()` methods that build and manage each toast
 * internally. Rendering `<MotifToast>` children declaratively produced a toast
 * whose underlying alert had no label ("Label not found") and never became
 * visible, so no toast (route-guard denials, popup denials, MFE alerts) ever
 * showed. We instead forward each new store item to the element's `addToast()`
 * exactly once; the toaster owns display + auto-dismiss and fires `onDismissed`,
 * which prunes the item from the store. A store item removed programmatically is
 * pulled from the toaster with `removeToast()`.
 *
 * Legacy parity: visually replaces `<app-toaster>`. Auto-dismiss after
 * `timeoutMs` (default 5000 ms; 0 keeps it open until the user closes it).
 */
export function ToasterOutlet() {
  const items = useToastStore((s) => s.items);
  const dismiss = useToastStore((s) => s.dismiss);
  const toasterRef = useRef<ToasterElement | null>(null);
  // Store id → toaster's internal id ('pending' until `addToast` resolves), so
  // each item is dispatched once and can be pulled back out on programmatic
  // dismissal.
  const dispatched = useRef(new Map<number, number | 'pending'>());

  useEffect(() => {
    const toaster = toasterRef.current;
    if (!toaster) return;

    // Add newly-queued items to the toaster exactly once.
    for (const t of items) {
      if (dispatched.current.has(t.id)) continue;
      dispatched.current.set(t.id, 'pending');
      // The message is the primary (title) text so the alert always has a
      // label; an explicit title becomes the heading with the message as body.
      void toaster
        .addToast(
          {
            title: t.title ?? t.message,
            supportText: t.title ? t.message : undefined,
            variant: t.variant,
            type: 'text',
            onDismissed: () => dismiss(t.id),
          },
          t.timeoutMs > 0 ? t.timeoutMs : Infinity,
        )
        .then((toasterId) => {
          if (dispatched.current.has(t.id)) dispatched.current.set(t.id, toasterId);
        });
    }

    // Pull toasts the store dropped (programmatic dismiss) out of the toaster.
    for (const [storeId, toasterId] of [...dispatched.current]) {
      if (items.some((t) => t.id === storeId)) continue;
      if (typeof toasterId === 'number') void toaster.removeToast(toasterId);
      dispatched.current.delete(storeId);
    }
  }, [items, dismiss]);

  return (
    <MotifToaster
      className="shell-toaster"
      ref={(el) => {
        toasterRef.current = el as unknown as ToasterElement | null;
      }}
    />
  );
}
